import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import config from '../../config/auth0';
import { useApiToken } from '../../hooks/useApiToken';

const ProfileMenu: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth0();
  const getAccessToken = useApiToken();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<any>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = async () => {
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileError(null);
    setProfileData(null);
    try {
      const accessToken = await getAccessToken();
      const res = await fetch(`${config.authServiceBaseUrl}/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch profile');
      const data = await res.json();
      setProfileData(data);
    } catch (err: any) {
      setProfileError(err.message || 'Failed to fetch profile');
    } finally {
      setProfileLoading(false);
    }
    handleMenuClose();
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin + '/login' } });
    handleMenuClose();
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <IconButton 
        onClick={handleMenuOpen} 
        size="small" 
        sx={{ 
          width: 40,
          height: 40,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <Avatar 
          src={user?.picture} 
          alt={user?.name || 'Profile'} 
          sx={{ 
            width: 32, 
            height: 32,
            fontSize: '0.875rem'
          }} 
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root': {
            mt: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2
          }
        }}
      >
        <MenuItem onClick={handleProfile}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
      <Dialog open={profileOpen} onClose={() => setProfileOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Profile</DialogTitle>
        <DialogContent>
          {profileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress />
            </Box>
          ) : profileError ? (
            <Typography color="error">{profileError}</Typography>
          ) : profileData ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Avatar src={user?.picture} alt={user?.name || 'Profile'} sx={{ width: 64, height: 64, mx: 'auto', mb: 2 }} />
              <Typography variant="h6">{profileData.name || user?.name}</Typography>
              <Typography variant="body2" color="text.secondary">{profileData.email || user?.email}</Typography>
              {/* Add more profile fields as needed */}
              <Box sx={{ mt: 2, textAlign: 'left' }}>
                <pre style={{ fontSize: 12, background: '#f8fafc', padding: 8, borderRadius: 6, overflowX: 'auto' }}>{JSON.stringify(profileData, null, 2)}</pre>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileMenu; 